<!--
 * @Description: 发票-蓝红票、补充材料、补充材料仅展示
                 上传、补充都时归属到当前发票类型下，可操作时都绑定为第一个元素，不可操作时对应下标绑定012展示
                 调用层控制是否展示
 * @Date: 2025-06-27 13:31:53
-->
<template>
  <BaseForm
    ref="formRef"
    label-position="top"
    scrollToError
    :model="modelValue"
    :rules="formRules"
  >
    <div class="form-dialog__title">
      <h1>
        {{
          importType
            ? `补充材料(${importType})`
            : `发票-${['', '蓝票', '红票'][invoiceFunction]}`
        }}
      </h1>
    </div>
    <div class="form-dialog__content" style="display: block">
      <BaseFormItem label="发票照片" prop="imgList">
        <div>
          <BaseUpload
            v-if="!disabled"
            style="margin-bottom: 20px"
            autoUpload
            btnText="上传"
            tipText="只能上传jpg/png，且每份不超过50MB"
            :listType="null"
            :accept="['.jpg', '.jpeg', '.png', '.JPG', '.JPEG', '.PNG']"
            :limit="20"
            :limitSize="50"
            :multiple="false"
            :showFileList="false"
            :http-request="uploadApi"
            @change="onImgChange"
          ></BaseUpload>
          <div
            style="display: flex; align-items: flex-end; margin-bottom: 30px"
            v-for="(img, imgIndex) in modelValue.imgList"
            :key="img.invoicePic"
          >
            <!-- 居左 -->
            <BaseImage
              style="width: 200px; height: 120px"
              :src="img.invoicePic"
              :preview-src-list="[img.invoicePic]"
            />
            <!-- 居右 -->
            <div
              style="
                margin-left: 20px;
                display: flex;
                align-items: flex-end;
                flex-wrap: wrap;
              "
            >
              <!-- 居上 -->
              <div v-if="isType7" style="width: 100%">
                <span style="margin-right: 3px; display: block">开票日期</span>
                <BaseDatePicker
                  style="width: 240px"
                  type="date"
                  validate-event
                  :disabled="disabled"
                  v-model="img.invoiceDate"
                />
              </div>
              <!-- 居下 -->
              <div
                style="
                  width: 100%;
                  display: flex;
                  align-items: flex-end;
                  flex-wrap: wrap;
                "
              >
                <div style="margin-right: 15px">
                  <span style="display: block">
                    票面金额{{ invoiceFunction === 2 ? '（负）' : '' }}
                  </span>
                  <BaseInputNumber
                    style="width: 240px; display: block"
                    placeholder="请输入"
                    :disabled="disabled"
                    :controls="false"
                    :precision="2"
                    :min="0"
                    :max="999999999"
                    v-model="img.invoiceAmount"
                  />
                </div>
                <div style="position: relative">
                  <span>发票号码</span>
                  <BaseInput
                    style="width: 240px"
                    :disabled="disabled"
                    v-model="img.invoiceNo"
                  />
                  <BaseIcon
                    v-if="!disabled"
                    size="18"
                    color="#ED7067"
                    style="
                      cursor: pointer;
                      position: absolute;
                      right: -40px;
                      bottom: 10px;
                    "
                    @click="onImgRemove(imgIndex)"
                  >
                    <DeleteFilled />
                  </BaseIcon>
                </div>
              </div>
            </div>
          </div>
        </div>
      </BaseFormItem>
      <BaseFormItem
        label="其他材料"
        prop="fileList"
        v-if="!disabled || modelValue.fileList?.length"
      >
        <BaseUpload
          style="margin-bottom: 20px"
          listType="text"
          autoUpload
          showFileList
          btnText="添加文件"
          :tipText="
            disabled ? '' : '只能上传png/jpg/jpeg/pdf/bmp文件，且每份不超过50MB'
          "
          :accept="['.jpg', '.jpeg', '.png', '.pdf', '.bmp']"
          :limit="disabled ? 1 : 10"
          :limitSize="50"
          :multiple="false"
          :http-request="uploadApi"
          :disabled="disabled"
          :uploadBtnProps="{
            limitOverHide: true
          }"
          v-model:fileList="modelValue.fileList"
          @preview="onFilePreview"
        ></BaseUpload>
      </BaseFormItem>
      <BaseFormItem label="是否邮寄" prop="isMail">
        <BaseRadioGroup :disabled="disabled" v-model="modelValue.detail.isMail">
          <BaseRadio
            v-for="item in Object.values(MAIL_TYPE)"
            :key="item.value"
            :value="item.value"
          >
            {{ item.label }}
          </BaseRadio>
        </BaseRadioGroup>
      </BaseFormItem>
      <div
        v-if="modelValue.detail.isMail === 1"
        style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px"
      >
        <BaseFormItem label="快递公司" prop="companyCode">
          <BaseSelect
            style="width: 100%"
            placeholder="请选择"
            init
            clearable
            filterUseRemote
            :remoteMethod="companyCodeRemote"
            :disabled="disabled"
            v-model="modelValue.detail.companyCode"
            @change="onCompanyCodeChange"
          ></BaseSelect>
        </BaseFormItem>
        <BaseFormItem label="快递单号" prop="trackingNo">
          <BaseInput
            style="width: 100%"
            placeholder="请输入"
            :disabled="disabled"
            v-model="modelValue.detail.trackingNo"
          />
        </BaseFormItem>
      </div>
    </div>
  </BaseForm>
</template>

<script lang="tsx" setup>
import { ref, computed } from 'vue';
import {
  BaseForm,
  BaseFormItem,
  BaseInput,
  BaseImage,
  BaseUpload,
  BaseIcon,
  BaseRadioGroup,
  BaseRadio,
  BaseSelect,
  BaseInputNumber,
  BaseDatePicker
} from 'xrk-components';
import { uploadApi } from '@data-source/common/apis';
import { InvoiceApplyDetailListDTO } from '@V/invoice/src/data-source/types';
import { logisticsCode } from '@V/invoice/src/data-source/apis';
import { DetailType, InvoiceFunction, ImportType } from '@V/invoice/src/types';
import { DeleteFilled } from '@element-plus/icons-vue';
import { MAIL_TYPE } from '@V/invoice/src/constants';
import { useRecognition } from '@V/invoice/src/hooks';
import { StateType } from '@cTypes/order';

type FormData = InvoiceApplyDetailListDTO;

const props = withDefaults(
  defineProps<{
    detailType: DetailType;
    id: number; // 发票申请详情id
    importType?: ImportType | number;
    invoiceFunction?: InvoiceFunction; // 1蓝票 2红票
    invoiceBusinessType?: StateType;
  }>(),
  {
    invoiceFunction: 1,
    importType: 0
  }
);

// 蓝、红禁用状态区分
const disabled = !(
  (props.detailType === 2 && props.invoiceFunction === 1) || // 蓝票可编辑
  (props.detailType === 3 && props.invoiceFunction === 2) || // 红票可编辑
  // 补充材料
  [4, 5, 11, 12].includes(props.detailType)
);
const modelValue = defineModel<FormData>(); // 集合中只会有一个元素
const formRef = ref(null);

const formRules = {
  imgList: [
    {
      required: true,
      trigger: 'change',
      validator: (rule, v, callback) => {
        if (!v?.length) {
          return callback(new Error('请上传发票'));
        }
        if (v.some(item => !item.invoicePic)) {
          return callback(new Error('请重新上传发票'));
        }
        if (v.some(item => !item.invoiceAmount || !item.invoiceNo)) {
          return callback(new Error('请填写发票信息'));
        }
        if (v.some(item => !item.invoiceDate && isType7.value)) {
          return callback(new Error('请填写开票日期'));
        }
        callback();
      }
    }
  ],
  fileList: [
    {
      required: false,
      trigger: 'change',
      validator: (rule, v, callback) => {
        if (v.some(item => !item.url)) {
          return callback(new Error('请重新上传资料'));
        }
        callback();
      }
    }
  ],
  isMail: [
    {
      required: true,
      trigger: 'change',
      validator: (rule, v, callback) => {
        const { isMail } = modelValue.value.detail;
        // 不确定后端是否有默认值
        if (
          !+Object.values(MAIL_TYPE)
            .map(item => item.value)
            .includes(isMail)
        ) {
          return callback(new Error('请选择邮寄方式'));
        }
        callback();
      }
    }
  ],
  trackingNo: [
    {
      required: true,
      trigger: 'change',
      validator: (rule, v, callback) => {
        const { isMail, trackingNo } = modelValue.value.detail;
        if (isMail === 1 && !trackingNo) {
          return callback(new Error('请填写单号'));
        }
        callback();
      }
    }
  ],
  companyCode: [
    {
      required: true,
      trigger: 'change',
      validator: (rule, v, callback) => {
        const { isMail, companyCode } = modelValue.value.detail;
        if (isMail === 1 && !companyCode) {
          return callback(new Error('请选择快递公司'));
        }
        callback();
      }
    }
  ]
};
// 个体相关类型
const isType7 = computed(() => [7, 9, 10].includes(props.invoiceBusinessType));

const companyCodeRemote = name => {
  return logisticsCode({
    name
  });
};

const onImgChange = async v => {
  const { response } = v;
  if (response && response.data.fileUrl) {
    const ocr = await useRecognition(response.data.fileUrl);
    modelValue.value.imgList.push({
      id: null,
      invoicePic: response.data.fileUrl,
      invoiceType: props.invoiceFunction,
      invoiceApplyId: props.id,
      remark: null,
      createAt: Date.now(),
      ...ocr
    });
  }
  formRef.value.validate('imgList');
};
const onImgRemove = index => {
  modelValue.value.imgList.splice(index, 1);
  formRef.value.validate('imgList');
};

const onCompanyCodeChange = (v, options) => {
  modelValue.value.detail.companyName = options[0].label;
  modelValue.value.detail.trackingNo = '';
};
const onFilePreview = v => {
  window.open(v.url);
};

defineExpose({
  validate: (...args: any) => formRef.value?.validate(...args)
});
</script>
